import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

import { useSetCurrentCrumbLabel } from "context/breadcrumb-entity-label-context";

export type SchoolOpsHeaderState = {
  actions?: React.ReactNode;
  tabs?: React.ReactNode;
  title?: string;
};

type SchoolOpsHeaderContextValue = {
  header: SchoolOpsHeaderState | null;
  setHeader: (header: SchoolOpsHeaderState | null) => void;
};

const SchoolOpsHeaderContext = createContext<SchoolOpsHeaderContextValue | null>(null);

const headersEqual = (left: SchoolOpsHeaderState | null, right: SchoolOpsHeaderState | null) => {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return left.title === right.title && left.tabs === right.tabs && left.actions === right.actions;
};

export const SchoolOpsHeaderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [header, setHeaderState] = useState<SchoolOpsHeaderState | null>(null);
  const setHeader = useCallback((next: SchoolOpsHeaderState | null) => {
    setHeaderState((previous) => (headersEqual(previous, next) ? previous : next));
  }, []);
  const value = useMemo(
    () => ({
      header,
      setHeader,
    }),
    [header, setHeader],
  );

  return (
    <SchoolOpsHeaderContext.Provider value={value}>{children}</SchoolOpsHeaderContext.Provider>
  );
};

export const useSchoolOpsHeaderContext = () => {
  const context = useContext(SchoolOpsHeaderContext);

  if (!context) {
    throw new Error("useSchoolOpsHeaderContext must be used within SchoolOpsHeaderProvider");
  }

  return context;
};

export const SchoolOpsHeader: React.FC<SchoolOpsHeaderState> = ({ actions, tabs, title }) => {
  const { setHeader } = useSchoolOpsHeaderContext();
  useSetCurrentCrumbLabel(title);

  useLayoutEffect(() => {
    if (!title && !tabs && !actions) {
      setHeader(null);
      return undefined;
    }

    setHeader({ actions, tabs, title });
    return () => setHeader(null);
  }, [actions, setHeader, tabs, title]);

  return null;
};
